import {ActionIcon, Box, Button, createStyles, Group, Stack, Text} from '@mantine/core';
import React, {useState} from 'react';

import {makeAnalyticsEventId, trackEventWithModule} from 'analytics/Analytics';
import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import ProteinCreationSearchModal, {
  ADD_FROM_SEARCH_TITLE,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSearchModal';
import {ProteinCreationSettingsData} from 'antibodies/nested-data-creation/full-screen/ProteinCreationSettingsUtil';
import ProteinCreationSpreadsheetImportModal from 'antibodies/nested-data-creation/full-screen/ProteinCreationSpreadsheetImportModal';
import ProteinCreationStructuredTableAdapter, {
  ProteinCreationStructuredTable,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationStructuredTableAdapter';
import {ProteinCreationRow} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  NdcSpreadsheetComponentFormatType,
  NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import NestedCreateAndRegisterAntibodiesSpreadsheetImportModal from 'antibodies/nested-data-creation/spreadsheet-import/NestedCreateAndRegisterAntibodiesSpreadsheetImportModal';
import Antibody from 'antibodies/visualization/Antibody';
import BenchlingConfig from 'BenchlingConfig';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  CSV_TYPE,
} from 'constants/AnalyticsConstants';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import addSamplesToTable from 'notebook/sample-propagation/util/addSamplesToTable';
import {
  StructuredTableAddFromSourceType,
  StructuredTableAddMenuItems,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import downloadStructuredTableCSV, {
  getStructuredTableCSVDownloadDisabledReason,
  TABLE_LOADING_CSV_DOWNLOAD_DISABLED_REASON,
} from 'notebook/structured-tables/util/downloadStructuredTableCSV';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import MantineDropdownMenu from 'ui-library/menus/MantineDropdownMenu';
import * as modals from 'util/Modals';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

const TABLE_CID = 'protein-creation-table';
export const REPLACE_ROWS_CONFIRMATION_TITLE = 'Replace existing rows?';
const REPLACE_ROWS_CONFIRMATION_MESSAGE =
  "Adding from a spreadsheet replaces all rows currently in the table. This can't be undone.";
const GLYPH_SIZE = 48;
const useStyles = createStyles((theme) => ({
  headerContainer: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderTopLeftRadius: theme.radius.md,
    borderTopRightRadius: theme.radius.md,
    backgroundColor: theme.white,
  },
  glyphWrapper: {
    width: GLYPH_SIZE,
    height: GLYPH_SIZE,
    flexShrink: 0,
  },
  downloadButton: {
    '&&': {
      marginLeft: 0,
    },
  },
}));

function TableHeader({
  format,
  downloadDisabledReason,
  onAddFromSearch,
  onAddFromWorklist,
  onAddFromSpreadsheet,
  onDownloadCSV,
  onOpenSettings,
  startingType,
}: {
  format: AntibodyFormat;
  downloadDisabledReason: string | null;
  onAddFromSearch: () => void;
  onAddFromWorklist: () => void;
  onAddFromSpreadsheet: () => void;
  onDownloadCSV: () => void;
  onOpenSettings: () => void;
  startingType: NdcSpreadsheetComponentFormatType;
}): JSX.Element {
  const {classes} = useStyles();
  const startingTypeLabel = NDC_SPREADSHEET_COMPONENT_FORMAT_TYPE_PLURAL[startingType];

  return (
    <Group
      position="apart"
      noWrap={true}
      px="lg"
      py="sm"
      mx="10px"
      mb="-12px"
      className={classes.headerContainer}
    >
      <Group spacing="md" noWrap={true}>
        <Box className={classes.glyphWrapper}>
          <Antibody format={format} />
        </Box>
        <Stack spacing={2}>
          <Text size="md" weight={600}>
            {format.name} {startingTypeLabel}
          </Text>
          <Text size="xs" c="dimmed" lh="16px">
            Fill each column with an existing Benchling entity or a new DNA or AA sequence. Type or paste
            directly into cells, or pull sequences in from search
          </Text>
        </Stack>
      </Group>
      <Group spacing="sm" noWrap={true}>
        <ActionIcon
          variant="tertiary"
          size="xs"
          aria-label="Protein creation settings"
          onClick={onOpenSettings}
        >
          <BenchlingIcon icon="settings" />
        </ActionIcon>
        <SimpleDownloadAsCSVButton
          className={classes.downloadButton}
          disabled={downloadDisabledReason != null}
          onDownloadAsCSV={onDownloadCSV}
          tooltipProps={downloadDisabledReason != null ? {title: downloadDisabledReason} : undefined}
        />
        <MantineDropdownMenu
          position="bottom-end"
          sections={[
            {
              items: [
                {name: ADD_FROM_SEARCH_TITLE, icon: 'search', onSelect: onAddFromSearch},
                ...(BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS
                  ? [
                      {
                        ...StructuredTableAddMenuItems[StructuredTableAddFromSourceType.ADD_FROM_WORKLIST],
                        onSelect: onAddFromWorklist,
                      },
                    ]
                  : []),
                {
                  ...StructuredTableAddMenuItems[StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET],
                  onSelect: onAddFromSpreadsheet,
                },
              ],
            },
          ]}
          trigger={
            <Button variant="secondary" rightIcon={<BenchlingIcon icon="chevron-down" />}>
              Add sequences
            </Button>
          }
        />
      </Group>
    </Group>
  );
}

/**
 * Returns why the protein creation table can't be downloaded as a CSV yet, or null if it can.
 */
export function getProteinCreationTableCSVDownloadDisabledReason({
  structuredTable,
  isSchemaLoaded,
  computedTable,
}: {
  structuredTable: ProteinCreationStructuredTable;
  isSchemaLoaded: boolean;
  computedTable: Array<Array<EvaluatedCell>> | null;
}): string | null {
  if (!isSchemaLoaded || computedTable == null) {
    return TABLE_LOADING_CSV_DOWNLOAD_DISABLED_REASON;
  }
  return getStructuredTableCSVDownloadDisabledReason({structuredTable, computedTable});
}

export function downloadProteinCreationTableCSV({
  structuredTable,
  computedTable,
}: {
  structuredTable: ProteinCreationStructuredTable;
  computedTable: Array<Array<EvaluatedCell>>;
}): void {
  trackEventWithModule(COMMON_ANALYTICS_ACTIONS.DOWNLOADED_CSV, ANALYTICS_MODULE.ANTIBODIES, {
    csvType: CSV_TYPE.PROTEIN_CREATION_TABLE,
  });
  downloadStructuredTableCSV({
    structuredTable,
    structuredTableAdapter: ProteinCreationStructuredTableAdapter.getAdapter(),
    computedTable,
  });
}

export interface ProteinCreationTableProps {
  creationSettings: ProteinCreationSettingsData;
  structuredTable: ProteinCreationStructuredTable;
  handleTableUpdate: (newTable: UnknownStructuredTable) => void;
  replaceRows: (newRows: Array<ProteinCreationRow>) => void;
  /** Whether any row has a value, in which case a spreadsheet import must be confirmed before it replaces them. */
  hasExistingRows: boolean;
  isSchemaLoaded: boolean;
  computedTable: Array<Array<EvaluatedCell>> | null;
  onComputedTableChanged: (computedTable: Array<Array<EvaluatedCell>>) => void;
}

export default function ProteinCreationTable({
  creationSettings,
  structuredTable,
  handleTableUpdate,
  replaceRows,
  hasExistingRows,
  isSchemaLoaded,
  computedTable,
  onComputedTableChanged,
}: ProteinCreationTableProps): JSX.Element {
  const {selectedFormat: format, selectedStartingType: startingType} = creationSettings;
  const [isSpreadsheetImportOpen, setIsSpreadsheetImportOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const downloadDisabledReason = getProteinCreationTableCSVDownloadDisabledReason({
    structuredTable,
    isSchemaLoaded,
    computedTable,
  });

  const onAddFromWorklist = (): void => {
    const adapter = ProteinCreationStructuredTableAdapter.getAdapter();
    addSamplesFromWorklist(
      structuredTable,
      adapter,
      (samplesByColumn) => {
        trackEventWithModule(
          ANTIBODIES_ANALYTICS_ACTIONS.PROTEIN_CREATION_ADDED_ENTITIES,
          ANALYTICS_MODULE.ANTIBODIES,
          {
            source: 'worklist',
            formatId: format.id,
            startingDataType: startingType,
            numberOfRows: samplesByColumn.flatMap(({samples}) => samples).length,
          }
        );
        const result = addSamplesToTable(
          structuredTable,
          adapter,
          adapter.toTable(structuredTable),
          samplesByColumn
        );
        if (result != null) {
          handleTableUpdate(result.updatedTable);
        }
      },
      WORKLIST_FILL_LOCATION.PROTEIN_CREATION_TABLE,
      {hideReplicates: true}
    );
  };

  const onAddFromSpreadsheet = async (): Promise<void> => {
    if (hasExistingRows) {
      const confirmed = await Promise.resolve(
        modals.confirm(
          REPLACE_ROWS_CONFIRMATION_TITLE,
          REPLACE_ROWS_CONFIRMATION_MESSAGE,
          ButtonStyle.NEGATIVE,
          {
            acceptText: 'Continue',
            rejectText: 'Cancel',
            enableRedesign: true,
            promiseCompatible: true,
          }
        )
      );
      if (!confirmed) {
        return;
      }
    }
    setIsSpreadsheetImportOpen(true);
  };

  return (
    <Stack spacing={0}>
      <TableHeader
        format={format}
        downloadDisabledReason={downloadDisabledReason}
        onAddFromSearch={() => {
          setIsSearchOpen(true);
        }}
        onAddFromWorklist={onAddFromWorklist}
        onAddFromSpreadsheet={onAddFromSpreadsheet}
        onDownloadCSV={() => {
          // The button is disabled until the table has been computed.
          if (computedTable == null) {
            return;
          }
          downloadProteinCreationTableCSV({structuredTable, computedTable});
        }}
        onOpenSettings={() => {
          legacyRunModal(NestedCreateAndRegisterAntibodiesSpreadsheetImportModal, {
            initialSettings: creationSettings,
            analyticsMetadata: {
              actionOrigin: ActionOrigin.ANTIBODY_NESTED_DATA_CREATION,
              eventGroupId: makeAnalyticsEventId(),
            },
          });
        }}
        startingType={startingType}
      />
      <StructuredTableOutsideNotebook
        structuredTable={structuredTable}
        cid={TABLE_CID}
        updateHandler={handleTableUpdate}
        onComputedTableChanged={onComputedTableChanged}
        enableColumnResizing={false}
        includeUnsubmittedStatus={false}
        hideColumnHeaderIcons={false}
        canRename={false}
        suppressTitleBar={true}
      />
      {isSpreadsheetImportOpen && (
        <ProteinCreationSpreadsheetImportModal
          columns={structuredTable.columns}
          format={format}
          onClose={() => {
            setIsSpreadsheetImportOpen(false);
          }}
          onImportRows={replaceRows}
          startingType={startingType}
        />
      )}
      {isSearchOpen && (
        <ProteinCreationSearchModal
          format={format}
          startingType={startingType}
          onClose={() => {
            setIsSearchOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
